import { useMemo, type ReactElement, type ReactNode } from 'react'
import { getInitials, getName, isSystemUser, type TinyUser, type WithUserId } from '@terros/common'
import { Text } from '@mantine/core'
import { getUserColor, useTinyUser, useTranslation } from '@front/model'
import { type InfoFetcherProps } from './types'
import { LoadingEntityInfo, PrefetchedEntityInfo } from './EntityInfo'
import { EntityHoverCard } from './EntityHoverCard'

type UserInfoProps = InfoFetcherProps & WithUserId

export const UserInfo = ({ userId, linked, ...avatarProps }: UserInfoProps): ReactNode => {
  const { t } = useTranslation()
  const { data, isLoading, error } = useTinyUser(userId)
  if (error)
    return (
      <Text c='dimmed' fs='italic'>
        {t('Unknown user')}
      </Text>
    )
  if (isLoading || !data) return <LoadingEntityInfo {...avatarProps} />

  return <PrefetchedUserInfo {...avatarProps} linked={linked} user={data} />
}

type UserInfoPrefetchedInput = InfoFetcherProps & {
  user: TinyUser
  companyName?: string
  avatar?: ReactNode
}

export const PrefetchedUserInfo = (props: UserInfoPrefetchedInput): ReactElement => {
  const { user, linked = true, ...rest } = props

  const name = getName(user)
  const initials = getInitials(user)
  const color = getUserColor(user)

  const entityInfo = {
    ...rest,
    avatarBlurhash: user.avatarBlurhash,
    avatarUrl: user.avatarUrl,
    isDeleted: user.isDeleted,
    name,
    initials,
    color,
  }

  const content = <PrefetchedEntityInfo {...entityInfo} />

  const shouldShowHoverCard = useMemo(() => {
    if (!linked) return false
    if (isSystemUser(user.userId)) return false
    return true
  }, [linked, user.userId])

  if (shouldShowHoverCard) {
    return (
      <EntityHoverCard
        {...entityInfo}
        subtitle={rest.companyName}
        href={`/user/${user.userId}`}
        metadata={[
          { icon: 'badge', label: 'User ID', value: user.userId, copyable: true },
          {
            icon: 'tag',
            label: 'Client User ID',
            value: user.clientUserId,
            copyable: true,
          },
        ]}
      >
        {content}
      </EntityHoverCard>
    )
  }
  return content
}
